import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Container, Box, Paper, Stack, Typography, Chip, IconButton, TextField, InputAdornment,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  LinearProgress, Fade, Grow, alpha, useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import CustomerImage from '../../media/Lamp Poses-08-1.png';

import { useApi } from '../../hooks/useApi';

/**
 * Customer Center landing page.
 *
 * Lists every account from cma/account with a chip showing how many
 * accountprofile entries exist for it. Click a row to drill into the
 * customer detail (CRUD on their profile entries).
 */
function Index() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [profilesByAccount, setProfilesByAccount] = useState({});
  const [classByAutotaskId, setClassByAutotaskId] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filter, setFilter] = useState('');

  const fetchData = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);

      // Pull accounts (required), profile entries (for counts), and Autotask
      // companies (for the classification column) in parallel. Only accounts
      // are critical — profiles and companies are best-effort so a vendor
      // hiccup leaves those columns blank rather than breaking the page.
      const [aRes, pRes, cRes] = await Promise.allSettled([
        jApi.request('GET', 'cma/account'),
        jApi.request('GET', 'cma/accountprofile'),
        jApi.request('GET', 'autotask/company'),
      ]);

      if (aRes.status === 'rejected') throw aRes.reason;
      const aList = Array.isArray(aRes.value.data) ? aRes.value.data : [];
      aList.sort((a, b) => (a.name || a.code || '').localeCompare(b.name || b.code || ''));
      setAccounts(aList);

      const pList = pRes.status === 'fulfilled' && Array.isArray(pRes.value.data) ? pRes.value.data : [];
      const counts = {};
      for (const p of pList) {
        if (!p.account) continue;
        counts[p.account] = (counts[p.account] || 0) + 1;
      }
      setProfilesByAccount(counts);

      const cList = cRes.status === 'fulfilled' && Array.isArray(cRes.value.data) ? cRes.value.data : [];
      const classMap = {};
      for (const c of cList) {
        if (c.id != null) classMap[String(c.id)] = c.classificationName || null;
      }
      setClassByAutotaskId(classMap);
    } catch (ex) {
      setErrorMessage(ex?.toString?.() || 'Failed to load customers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  // Hide former customers (by Autotask classification) from the list entirely.
  const nonFormer = useMemo(
    () => accounts.filter(a => classByAutotaskId[String(a.autotask_id)] !== 'Former Customer'),
    [accounts, classByAutotaskId]
  );

  const filtered = useMemo(() => {
    if (!filter.trim()) return nonFormer;
    const needle = filter.trim().toLowerCase();
    return nonFormer.filter(a => {
      const cls = classByAutotaskId[String(a.autotask_id)] || '';
      return (a.name || '').toLowerCase().includes(needle) ||
        (a.code || '').toLowerCase().includes(needle) ||
        cls.toLowerCase().includes(needle);
    });
  }, [nonFormer, filter, classByAutotaskId]);

  const headerGradient = {
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    borderRadius: 3,
    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
    p: 2,
    position: 'relative',
    overflow: 'hidden',
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5}
              sx={{ display: 'inline-flex', px: 2, py: 1, borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <HomeRoundedIcon sx={{ fontSize: 18, color: theme.palette.secondary.main, mr: 0.5 }}/>
                <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>Home</Typography>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Customer Center</Typography>
            </Stack>
          </Box>
        </Fade>

        <Grow in timeout={700}>
          <Box sx={headerGradient}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
              <Box
                sx={{
                  width: 50, height: 50, borderRadius: 2, overflow: 'hidden',
                  boxShadow: `0 4px 8px ${alpha(theme.palette.common.black, 0.2)}`,
                  border: `2px solid ${alpha(theme.palette.secondary.main, 0.3)}`,
                  flexShrink: 0,
                }}
              >
                <img width="100%" height="100%" src={CustomerImage} alt="Customer Center" style={{ objectFit: 'cover' }}/>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{
                  fontWeight: 700,
                  background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.primary.main} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  Customer Center
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isLoading ? 'Loading…' : `${nonFormer.length} customer${nonFormer.length === 1 ? '' : 's'} · ${Object.values(profilesByAccount).reduce((a,b)=>a+b,0)} profile entries`}
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Filter…"
                value={filter}
                onChange={e => setFilter(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>,
                }}
                sx={{ minWidth: 220 }}
              />
              <IconButton size="small" onClick={fetchData} disabled={isLoading}><RefreshIcon/></IconButton>
            </Stack>
            {isLoading && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}/>}
          </Box>
        </Grow>

        {errorMessage && (
          <Paper sx={{ p: 2, mt: 2, background: alpha(theme.palette.error.main, 0.1),
            border: `1px solid ${alpha(theme.palette.error.main, 0.3)}` }}>
            <Typography variant="body2" color="error.main">{errorMessage}</Typography>
          </Paper>
        )}

        {!isLoading && filtered.length === 0 && (
          <Paper sx={{ p: 4, mt: 2, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {filter ? 'No customers match that filter.' : 'No customers found.'}
            </Typography>
          </Paper>
        )}

        {!isLoading && filtered.length > 0 && (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Customer</TableCell>
                  <TableCell sx={{ width: 140 }}>Account</TableCell>
                  <TableCell sx={{ width: 200 }}>Classification</TableCell>
                  <TableCell align="center" sx={{ width: 160 }}>Profile entries</TableCell>
                  <TableCell align="center" sx={{ width: 120 }}>Active</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map(a => {
                  const count = profilesByAccount[a.code] || 0;
                  return (
                    <TableRow key={a.id || a.code} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/customer/${encodeURIComponent(a.code)}`)}>
                      <TableCell sx={{ fontWeight: 600 }}>{a.name || a.code}</TableCell>
                      <TableCell><code>{a.code}</code></TableCell>
                      <TableCell>
                        {(() => {
                          const cls = a.autotask_id != null ? classByAutotaskId[String(a.autotask_id)] : null;
                          return cls
                            ? <Chip size="small" variant="outlined" label={cls} />
                            : <Typography variant="body2" color="text.secondary">—</Typography>;
                        })()}
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small"
                          label={count}
                          color={count > 0 ? 'secondary' : 'default'}
                          variant={count > 0 ? 'filled' : 'outlined'}/>
                      </TableCell>
                      <TableCell align="center">
                        <Chip size="small"
                          label={a.active === false ? 'Inactive' : 'Active'}
                          color={a.active === false ? 'default' : 'success'}/>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
}

export default Index;
